import { CalendarField, CalendarPeriod } from '../school-calendar/school-calendar.types';
import { InfoScreenData } from '../types/InfoScreenData';

/**
 * Types for the infoscreen module (`?screenId=`). Mirrors `InfoscreenFeedResponse` in
 * `Abook.DomainModel/Dtos/Infoscreen/InfoscreenDtos.cs`; enums arrive as their names.
 */
export type InfoscreenViewType = 'List' | 'WeekCalendar' | 'DayCalendar' | 'Room' | 'LockerRoom' | 'Welcome';

/** A welcome pane beside the view: none, on the right, or on the left. */
export type InfoscreenSplitSide = 'None' | 'Right' | 'Left';

/** The welcome text's or body text's size, against the view's own. */
export type InfoscreenTextSize = 'Small' | 'Normal' | 'Large';
/** How the welcome text or body text lines up. */
export type InfoscreenTextAlign = 'Left' | 'Center' | 'Right';

/** List view only: scroll vertically, or show a page at a time. */
export type InfoscreenListMode = 'Scroll' | 'Pages';

/** The screen's basic colour set-up. */
export type InfoscreenColorTheme =
  | 'Classic'
  | 'Dark'
  | 'Light'
  | 'Halloween'
  | 'Winter'
  | 'Christmas'
  | 'Spring'
  | 'Autumn'
  | 'Summer'
  | 'DayCycleClassic'
  | 'DayCycleLightDark'
  | 'DayCycleYear'
  | 'Easter'
  | 'NewYear'
  | 'Eid'
  | 'LunarNewYear'
  | 'Diwali'
  | 'Fastelavn'
  | 'SanktHans';

/** The holidays "Følger året" (and the borrowed decorations) mark. */
export type InfoscreenHoliday =
  | 'Fastelavn'
  | 'Easter'
  | 'SanktHans'
  | 'Halloween'
  | 'Christmas'
  | 'NewYear'
  | 'Eid'
  | 'LunarNewYear'
  | 'Diwali';

/** The size a screen is drawn at; a fixed size is scaled to fill the window. */
export type InfoscreenScreenSize =
  | 'Auto'
  | 'LandscapeHd'
  | 'Landscape4k'
  | 'PortraitHd'
  | 'Portrait4k'
  | 'SmallLandscape'
  | 'SmallPortrait';

export const SCREEN_SIZES: Record<Exclude<InfoscreenScreenSize, 'Auto'>, { width: number; height: number }> = {
  LandscapeHd: { width: 1920, height: 1080 },
  Landscape4k: { width: 3840, height: 2160 },
  PortraitHd: { width: 1080, height: 1920 },
  Portrait4k: { width: 2160, height: 3840 },
  SmallLandscape: { width: 1280, height: 800 },
  SmallPortrait: { width: 800, height: 1280 },
};

export type InfoscreenPeriod = CalendarPeriod;

/** The kinds of weather the screens draw. */
export type InfoscreenWeatherCondition =
  | 'Clear'
  | 'Fair'
  | 'PartlyCloudy'
  | 'Cloudy'
  | 'Fog'
  | 'Rain'
  | 'Sleet'
  | 'Snow'
  | 'Thunder';

export interface InfoscreenWeatherHour {
  time: string;
  temperature: number;
  condition: InfoscreenWeatherCondition;
  night: boolean;
  /** Millimetres in the hour. */
  precipitation: number;
}

/** The weather now and the coming hours (MET Norway, via the API). */
export interface InfoscreenWeather {
  temperature: number;
  condition: InfoscreenWeatherCondition;
  night: boolean;
  /** Metres per second, and where it comes from in degrees (0 north, 90 east). */
  windSpeed: number;
  windFromDirection: number;
  hours: InfoscreenWeatherHour[];
  /** When the moon rises and sets today and tomorrow, and its phase in degrees (0 new, 180 full). */
  moonRises?: string[];
  moonSets?: string[];
  moonPhase?: number | null;
  source: string;
}

export interface InfoscreenNewsItem {
  title: string;
  published: string | null;
  /** Where it is from: "TV SYD", "Kolding Kommune". */
  source: string | null;
}

export interface InfoscreenFeedAd {
  id: number;
  /** An ad; or, made by the screen itself, the weather or the news shown among the ads. */
  type: 'Image' | 'Text' | 'Weather' | 'News';
  imageUrl: string | null;
  headline: string | null;
  body: string | null;
  backgroundColor: string | null;
  textColor: string | null;
}

export interface InfoscreenFeedMessage {
  id: number;
  text: string;
  severity: 'Info' | 'Important';
}

/** Today's resolved opening hours — `OpeningHoursDay` in the API. */
export interface OpeningHoursToday {
  date: string;
  isOpen: boolean;
  intervals: { opens: string; closes: string }[];
  /** `closed-day`, `holiday` or `blocked` (a blocking can take all or part of the day). */
  closedReason: string | null;
  /** The holiday's name or the blocking's type, when there is one. */
  closedLabel: string | null;
}

/** What a list column (or a line under it) shows. */
export type InfoscreenListField = CalendarField;

/** One column of the list view: a field shown large, and small lines under it. */
export interface InfoscreenListColumn {
  field: InfoscreenListField;
  details: InfoscreenListField[];
}

export interface InfoscreenFeed {
  screenId: number;
  locationId: number;
  locationName: string | null;
  viewType: InfoscreenViewType;
  listMode: InfoscreenListMode;
  screenSize: InfoscreenScreenSize;
  colorTheme: InfoscreenColorTheme;
  /** Percent; below 100 fits more on the screen. */
  zoom: number;
  /** The meeting room's name, for the Room view. */
  roomName: string | null;
  /** 1 (slow) – 10 (fast). */
  listScrollSpeed: number;
  /** 1 (slow) – 10 (fast). */
  adScrollSpeed: number;
  pageSeconds: number;
  from: string;
  /** Null when no opening hours are set up for the day. */
  openingHoursToday: OpeningHoursToday | null;
  /** The list view's columns (the standard four unless the screen has its own). */
  listColumns: InfoscreenListColumn[];
  /** The calendar boxes' lines, or what the meeting-room view writes at each meeting. */
  cardFields: InfoscreenListField[];
  /** The welcome view's text, or the welcome pane's. */
  welcomeText: string | null;
  /** The body text under it, smaller; null for none. */
  welcomeBody?: string | null;
  /** The two texts' size and alignment; normal and centred when not sent. */
  welcomeTextSize?: InfoscreenTextSize;
  welcomeTextAlign?: InfoscreenTextAlign;
  welcomeBodySize?: InfoscreenTextSize;
  welcomeBodyAlign?: InfoscreenTextAlign;
  /** A welcome pane beside the times, and its share of the width in percent. */
  splitSide: InfoscreenSplitSide;
  splitPercent: number;
  /**
   * The weather, when the screen shows it or draws it (its sky, the sprinkle in the wind);
   * `showWeather` says whether the temperature and forecast show. The forecast: an ad or a page.
   */
  weather: InfoscreenWeather | null;
  showWeather: boolean;
  weatherMode: 'Ad' | 'Page';
  /** The seasonal decorations (figures along the edges, the falling sprinkle) on. */
  showDecorations: boolean;
  /**
   * The decorations only at the top, by the date and clock: the bottom band as a divider under
   * the header, the sprinkle and the passers-by above it. Missing: along the edges.
   */
  decorAtTop?: boolean;
  /** The holidays the screen leaves out of its decorations; those days are the season's. */
  hiddenHolidays?: InfoscreenHoliday[];
  /** The sky over the top (sun or moon, clouds, weather); missing, it follows the theme. */
  showSun?: boolean;
  /** The date and the clock at the top; missing: shown. */
  showHeader?: boolean;
  /** Demo: the screen goes through the themes and layouts by itself (`demo-presets.ts`). */
  demo?: boolean;
  /** The headlines (newest first), when the screen shows news, and the sources' names. */
  news: InfoscreenNewsItem[];
  newsSourceName: string | null;
  /** A running line, or cards among the ads. */
  newsMode: 'Ticker' | 'WithAds';
  periods: InfoscreenPeriod[];
  ads: InfoscreenFeedAd[];
  messages: InfoscreenFeedMessage[];
}

export interface InfoscreenLegacyScreen {
  screenId: number | null;
}
